import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  input,
  model,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Product, ProductsService } from '@ubie/core';
import { ProductsListComponent } from '../../products/products-list/products-list.component';

type SelectedProduct = Product & { isSelected: boolean };

@Component({
  selector: 'acl-installation-payment',
  standalone: false,
  templateUrl: './installation-payment.component.html',
  styleUrl: './installation-payment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationPaymentComponent implements OnInit {
  private readonly productsService = inject(ProductsService);
  private readonly modalController = inject(ModalController);

  // current credits owned by the installer
  readonly currentCredits = signal(0);
  readonly currentCreditsName = signal('');

  // products that can be bought through online payment via mollie
  readonly paymentProducts = signal<SelectedProduct[]>([]);
  // products that can be bought with base products
  readonly productsToBuyWithPaymentProducts = signal<SelectedProduct[]>([]);

  readonly showUnlimited = computed(() => {
    const date = this.installerAccessUntil();
    if (date) {
      const minUnlimitedDate = new Date();
      minUnlimitedDate.setFullYear(minUnlimitedDate.getFullYear() + 100);

      if (minUnlimitedDate < new Date(date)) {
        return true;
      }
      return false;
    }
    return false;
  });

  @Input() set reload(isReload: boolean) {
    if (isReload) {
      this.initComponent();
    }
  }

  // Emit an event when a product was chosen
  @Output() readonly productSelected: EventEmitter<Product> =
    new EventEmitter<Product>();

  readonly btnText = input('Continue');
  readonly selectedProduct = model<Product | undefined>(undefined);

  readonly installerAccessUntil = input<Date | undefined>(undefined);

  ngOnInit(): void {
    this.initComponent();
  }

  initComponent(): void {
    this.showCreditInfo();
    this.showProductsToBuyWithPaymentProducts();
  }

  /**
   * Show current credits for the current installer (currently this is the only base product)
   */
  private showCreditInfo(): void {
    // get owned base products -- for idb currently only the credit is a base product
    this.getOwnedPaymentProducts();
  }

  private getOwnedPaymentProducts(): void {
    this.productsService.getOwnedPaymentProductsBySite().subscribe(
      (result) => {
        // TODO: when we allow different kinds of products on the admin, add a check for the product type
        if (result.length === 1) {
          if (result[0].amount !== undefined && result[0].amount !== null) {
            this.currentCredits.set(result[0].amount);
            this.currentCreditsName.set(result[0].name);
          }
        }
      },
      (error) => {
        console.error('could not get installations count', error);
      },
    );
  }

  /**
   * Get all base products that can be sold by this site
   */
  showProductsToBuyWithPaymentProducts(): void {
    this.productsService.getProductsToBuyWithPaymentProductsBySite().subscribe({
      next: (result) => {
        this.productsToBuyWithPaymentProducts.set(
          result.map((item) => {
            return {
              ...item,
              isSelected: item.uid === this.selectedProduct()?.uid,
            } as SelectedProduct;
          }),
        );
      },
      error: (error) => {
        console.error('could not get installations count', error);
      },
    });
  }

  /**
   * When a product has been selected to be bought
   * @param product
   */
  onProductSelected(product: SelectedProduct): void {
    console.error('selected product', product);
    if (product.pricePaymentProduct === undefined) {
      return;
    }

    // check if we have enough credits to buy the product
    if (product.pricePaymentProduct <= this.currentCredits()) {
      // remove all highlights

      this.productsToBuyWithPaymentProducts.update((current) => {
        return current.map((item) => {
          return {
            ...item,
            isSelected: item.uid === product.uid,
          };
        });
      });

      // emit event
      this.selectedProduct.set(product);
    } else {
      // insufficient base products, show message
      this.showPaymentProductsToBuy(
        'installation_buyCredits',
        'installation_buyCreditsInsufficient',
      );
    }
  }

  /**
   * Get all base products that can be sold by this site
   */
  showPaymentProductsToBuy(title: string, intro: string): void {
    if (this.paymentProducts().length === 0) {
      this.productsService.getPaymentProductsBySite().subscribe({
        next: (result) => {
          const selectedProducts = result.map((item) => ({
            ...item,
            isSelected: false,
          }));
          this.paymentProducts.set(selectedProducts);
          this.showProductModal(title, intro);
        },
        error: (error) => {
          // error fetching data
        },
      });
    } else {
      this.showProductModal(title, intro);
    }
  }

  /**
   * Show modal to sell products
   * @param products
   */
  async showProductModal(title: string, intro: string): Promise<void> {
    const modal = await this.modalController.create({
      component: ProductsListComponent,
      cssClass: 'products-modal',
      componentProps: {
        products: this.paymentProducts(),
        title: title,
        intro: intro,
      },
    });
    return await modal.present();
  }

  submitProduct(): void {
    this.productSelected.emit(this.selectedProduct());
  }
}
